/**
 * Crop color system — group-based with HSL shade variation.
 *
 * Architecture rationale:
 * - 11 crop groups (cereals, oilseeds, rootcrops, corn, legumes, grassland,
 *   leafyveg, fruitveg, rootveg, berries, special)
 * - Each crop in a group shares a hue but varies in lightness (shade)
 * - Algorithmic derivation: pre-attentive color discrimination favors
 *   ~7-12 distinct color families over 30+ individual hues
 * - Future special crops (Salat, Karotten, Kohlrabi, etc.) plug into
 *   existing groups without designer involvement
 *
 * Quality:
 * - WCAG-AA contrast: fg color auto-selected for readability on bg
 * - Backend-canonical crop codes (20 from backend/i18n/crop_names.py)
 * - Aliases handled transparently
 *
 * Usage:
 *   import { getCropColor } from '../../config/cropColors';
 *   const { bg, fg, group } = getCropColor('wheat');
 *   <div style={{ backgroundColor: bg, color: fg }}>Wheat field</div>
 */

// ============================================================
// Group base palette — HSL for clean shade derivation
// ============================================================

const GROUP_PALETTE = {
  cereals:   { hue: 42,  saturation: 75, lightness: 55 },  // Goldgelb (Weizenfeld)
  oilseeds:  { hue: 50,  saturation: 90, lightness: 55 },  // Sonnengelb (Raps)
  rootcrops: { hue: 25,  saturation: 50, lightness: 38 },  // Erdbraun (Kartoffel/Rübe)
  corn:      { hue: 32,  saturation: 85, lightness: 55 },  // Mais-Orange
  legumes:   { hue: 130, saturation: 50, lightness: 45 },  // Blattgrün
  grassland: { hue: 95,  saturation: 60, lightness: 50 },  // Frischgrün (Wiese)
  leafyveg:  { hue: 80,  saturation: 55, lightness: 60 },  // Hellgrün-Gelb
  fruitveg:  { hue: 5,   saturation: 70, lightness: 55 },  // Tomatenrot
  rootveg:   { hue: 18,  saturation: 75, lightness: 55 },  // Möhrenorange
  berries:   { hue: 280, saturation: 50, lightness: 50 },  // Beeren-Lila
  special:   { hue: 100, saturation: 25, lightness: 50 },  // Salbei-Grau-Grün
};

// ============================================================
// Crop catalog — 20 backend-canonical + future-ready slots
// ============================================================

const CROP_CATALOG = {
  // Cereals (Getreide) — 6 entries
  wheat:     { group: 'cereals',   shade: 0   },
  barley:    { group: 'cereals',   shade: -10 },
  rye:       { group: 'cereals',   shade: -18 },
  oats:      { group: 'cereals',   shade: 8   },
  triticale: { group: 'cereals',   shade: -14 },
  spelt:     { group: 'cereals',   shade: 12  },

  // Oilseeds (Ölfrüchte) — 3 entries
  rapeseed:  { group: 'oilseeds',  shade: 0   },
  sunflower: { group: 'oilseeds',  shade: -8  },
  flax:      { group: 'oilseeds',  shade: 14  },

  // Root crops (Hackfrüchte) — 2 entries
  potato:    { group: 'rootcrops', shade: 0   },
  sugarbeet: { group: 'rootcrops', shade: -10 },

  // Corn (Mais) — own family, shade=0 always
  corn:      { group: 'corn',      shade: 0   },

  // Legumes (Hülsenfrüchte) — 6 entries
  peas:        { group: 'legumes', shade: 0   },
  faba_beans:  { group: 'legumes', shade: -10 },
  soybeans:    { group: 'legumes', shade: -16 },
  lupins:      { group: 'legumes', shade: 12  },
  clover:      { group: 'legumes', shade: 8   },
  alfalfa:     { group: 'legumes', shade: -6  },

  // Grassland (Grünland) — 1 entry
  grassland: { group: 'grassland', shade: 0   },

  // Special / herbs / fiber — 1 entry currently
  hemp:      { group: 'special',   shade: 0   },

  // ---- Future-ready slots (uncomment as backend introduces them) ----
  // Sonderkulturen patterns ready for plug-in:
  //
  // Leafy vegetables (Blattgemüse)
  // salad:        { group: 'leafyveg', shade: 0   },
  // spinach:      { group: 'leafyveg', shade: -10 },
  // kohlrabi:     { group: 'leafyveg', shade: 6   },
  // cabbage:      { group: 'leafyveg', shade: -6  },
  //
  // Fruit vegetables (Fruchtgemüse)
  // tomato:       { group: 'fruitveg', shade: 0   },
  // pepper:       { group: 'fruitveg', shade: -8  },
  // squash:       { group: 'fruitveg', shade: 8   },
  // pumpkin:      { group: 'fruitveg', shade: 12  },
  //
  // Root vegetables (Wurzelgemüse)
  // carrot:       { group: 'rootveg',  shade: 0   },
  // parsnip:      { group: 'rootveg',  shade: 10  },
  // radish:       { group: 'rootveg',  shade: -12 },
  // onion:        { group: 'rootveg',  shade: 6   },
  //
  // Berries (Beerenobst)
  // strawberry:   { group: 'berries',  shade: 0   },
  // blueberry:    { group: 'berries',  shade: -12 },
  // raspberry:    { group: 'berries',  shade: 8   },
};

// ============================================================
// Aliases — non-canonical strings mapped to canonical
// ============================================================

const CROP_ALIASES = {
  // Old plural/singular variants
  bean: 'faba_beans',
  pea: 'peas',
  soybean: 'soybeans',
  // Generic fallback alias
  legume: 'peas',
};

// ============================================================
// Defaults
// ============================================================

const FALLBACK_COLOR = {
  bg: '#94a3b8',  // slate-400 — neutral, visible, not eye-catching
  fg: '#ffffff',
  group: null,
};

// ============================================================
// Helpers
// ============================================================

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

/**
 * Choose foreground color for WCAG-AA contrast against an HSL background.
 * Heuristic: lightness > 60 → dark text, otherwise white text.
 * For HSL, lightness is a reasonable proxy for perceived brightness.
 */
function chooseForeground(lightness) {
  return lightness >= 60 ? '#1a1a1a' : '#ffffff';
}

function hslString(hue, saturation, lightness) {
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

// ============================================================
// Public API
// ============================================================

/**
 * Resolve a crop code (canonical or alias) to its color.
 *
 * @param {string|null|undefined} cropCode
 * @returns {{ bg: string, fg: string, group: string|null }}
 */
export function getCropColor(cropCode) {
  if (!cropCode) return FALLBACK_COLOR;

  const lookup = String(cropCode).toLowerCase().trim();
  const resolved = CROP_ALIASES[lookup] || lookup;
  const entry = CROP_CATALOG[resolved];

  if (!entry) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[cropColors] Unknown crop code: "${cropCode}". Using fallback.`);
    }
    return FALLBACK_COLOR;
  }

  const groupBase = GROUP_PALETTE[entry.group];
  if (!groupBase) {
    return FALLBACK_COLOR;
  }

  const lightness = clamp(groupBase.lightness + entry.shade, 25, 80);
  const bg = hslString(groupBase.hue, groupBase.saturation, lightness);
  const fg = chooseForeground(lightness);

  return { bg, fg, group: entry.group };
}

/**
 * Get the list of all known crop codes (canonical only, excludes aliases).
 * Useful for UI components that render crop pickers or legends.
 *
 * @returns {string[]}
 */
export function getKnownCropCodes() {
  return Object.keys(CROP_CATALOG);
}

/**
 * Get the group base color (without shade variation).
 * Useful for legends or group-level UI.
 *
 * @param {string} groupName
 * @returns {{ bg: string, fg: string } | null}
 */
export function getGroupColor(groupName) {
  const base = GROUP_PALETTE[groupName];
  if (!base) return null;
  const bg = hslString(base.hue, base.saturation, base.lightness);
  const fg = chooseForeground(base.lightness);
  return { bg, fg };
}

// Internal exports for tests
export const _internal = {
  GROUP_PALETTE,
  CROP_CATALOG,
  CROP_ALIASES,
  FALLBACK_COLOR,
  clamp,
  chooseForeground,
  hslString,
};
